import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, RefreshCw, Link2, X } from 'lucide-react';
import { PageHeader } from '@/components/layout';
import { Button, Card, CardContent, CursorPagination, TableSkeleton } from '@/components/ui';
import { ApiErrorDisplay } from '@/components/features/error';
import { ServiceCard, ServiceTable, ServicesViewToggle, ServiceFilterBar, ServiceForm, DeleteConfirmation } from '@/components/features/services';
import { useServices, useCreateService, useUpdateService, useDeleteService } from '@/hooks/use-services';
import { useServiceFilterParams } from '@/hooks/use-service-filter-params';
import { useServicesViewMode } from '@/hooks/use-services-view-mode';
import { useAuth } from '@/hooks/use-auth';
import { useMerchantScope } from '@/hooks/use-merchant-scope';
import { ROLE_GROUPS } from '@/types/auth';
import type { Service, CreateServiceRequest, UpdateServiceRequest } from '@/types/service';

/**
 * Services list page.
 * Route: /services
 *
 * Features:
 * - List all services in a card grid
 * - Create new service via dialog
 * - Edit existing service via dialog
 * - Delete (deactivate) service with confirmation
 */
export function ServicesPage() {
  const { t } = useTranslation('services');
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const canWrite = hasRole(ROLE_GROUPS.SERVICE_WRITE);
  // Spec 0220: eine Wahrheitsquelle fuer Merchant-Sichtbarkeit (statt hasRole(PLATFORM)).
  const { showMerchant } = useMerchantScope();
  // Spec 352 §4: persistierter Kachel↔Tabelle-Umschalter (localStorage, nicht URL).
  const [viewMode, setViewMode] = useServicesViewMode();

  // Spec 352 §3: BFF-abgeleitete Filterleiste, URL als Quelle der Wahrheit. Der
  // frueher page-lokale Kunden-Filter (#314) ist hierin absorbiert — kein
  // client-seitiges services.filter() mehr, die Filter laufen serverseitig.
  const { filters, hasActiveFilters, setMerchant, setGw, setFlow, setIqServiceId, reset } =
    useServiceFilterParams();

  // Blaetter-Cursor (Spec 352 §0). Der Cursor gehoert zu EINEM Filter-Stand: aendert
  // sich der Filter — egal wie (Setter, Back-/Forward-Button, eingefuegter Link) — gilt
  // ein gespeicherter Cursor nicht mehr, und der Pager faellt rein ABGELEITET auf Seite 1
  // zurueck. So bleibt die URL die Quelle der Wahrheit, ohne dass ein veralteter Cursor je
  // einen falschen (teuren) gefilterten Scan ausloest. Kein useEffect, kein verschwendeter
  // Fetch — `useServices` wird pro Render genau einmal mit dem korrekten Cursor gerufen.
  const filterKey = `${filters.merchantId ?? ''}|${filters.gatewayServiceId ?? ''}|${filters.flowType ?? ''}|${filters.iqServiceId ?? ''}`;
  const [pager, setPager] = useState<{ key: string; stack: string[] }>({ key: filterKey, stack: [] });
  const cursorBelongsToFilter = pager.key === filterKey;
  const currentCursor =
    cursorBelongsToFilter && pager.stack.length > 0 ? pager.stack[pager.stack.length - 1] : undefined;
  const hasPreviousPage = cursorBelongsToFilter && pager.stack.length > 0;

  // Data fetching — Filter (API-shaped) + abgeleiteter Blaetter-Cursor.
  const { data, isLoading, error, refetch } = useServices({ ...filters, cursor: currentCursor });

  const handleNextPage = useCallback(
    (cursor: string) =>
      setPager((p) =>
        p.key === filterKey
          ? { key: filterKey, stack: [...p.stack, cursor] }
          : { key: filterKey, stack: [cursor] },
      ),
    [filterKey],
  );
  const handlePreviousPage = useCallback(
    () =>
      setPager((p) =>
        p.key === filterKey
          ? { key: filterKey, stack: p.stack.slice(0, -1) }
          : { key: filterKey, stack: [] },
      ),
    [filterKey],
  );

  // Mutations
  const createMutation = useCreateService();
  const updateMutation = useUpdateService();
  const deleteMutation = useDeleteService();

  // Dialog state
  const [formOpen, setFormOpen] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingService, setDeletingService] = useState<Service | null>(null);

  // Spec 352 §6: Kontext-Banner beim gw-Drill-down. Dismissibel — der dismissierte
  // gw-Wert wird gemerkt, sodass ein NEUER gw-Filter den Banner wieder zeigt; der
  // gw-Filter selbst bleibt (nur der transiente Banner verschwindet).
  const [dismissedGwBanner, setDismissedGwBanner] = useState<string | null>(null);

  const services = data?.data ?? [];
  // Banner-Zahlen kommen AUSSCHLIESSLICH aus dem BFF-Feld (volle Treffermenge),
  // nie aus dem FE-Page-Slice (der unterzaehlte ueber Seitengrenzen).
  const gwContext = data?.appliedFilters?.gwContext;
  // Bei degradiertem Scan (`unavailable`) ist das gwContext-Aggregat 0/0 — kein
  // irrefuehrendes "0 Services" neben dem "nicht ermittelbar"-Degradations-Banner
  // zeigen. Die Filterleiste rendert in dem Fall ohnehin den Unavailable-Hinweis.
  const showGwBanner =
    !!filters.gatewayServiceId &&
    !!gwContext &&
    !data?.unavailable &&
    dismissedGwBanner !== filters.gatewayServiceId;

  // Handlers
  const handleCreateClick = () => {
    setEditingService(null);
    setFormOpen(true);
  };

  const handleEditClick = (service: Service) => {
    setEditingService(service);
    setFormOpen(true);
  };

  const handleDeleteClick = (service: Service) => {
    setDeletingService(service);
    setDeleteDialogOpen(true);
  };

  const handleCreate = (data: CreateServiceRequest) => {
    createMutation.mutate(data, {
      onSuccess: (createdService) => {
        setFormOpen(false);
        // Spec 0216 §5.2: Auto-Redirect auf die Routing-View, damit der
        // Operator die Fallback-Kette direkt erweitern kann. Der
        // `?from=create`-Marker triggert dort den persistenten Banner.
        navigate(`/services/${createdService.serviceId}/routing?from=create`, {
          state: { merchantId: createdService.merchantId },
        });
      },
    });
  };

  const handleUpdate = (serviceId: string, data: UpdateServiceRequest) => {
    updateMutation.mutate(
      { serviceId, data },
      {
        onSuccess: () => {
          setFormOpen(false);
          setEditingService(null);
        },
      }
    );
  };

  const handleDeleteConfirm = () => {
    if (!deletingService) return;
    deleteMutation.mutate(deletingService.serviceId, {
      onSuccess: () => {
        setDeleteDialogOpen(false);
        setDeletingService(null);
      },
    });
  };

  return (
    <div>
      <PageHeader
        title={t('page.title')}
        description={t('page.description')}
        actions={
          <div className="flex items-center gap-2">
            <ServicesViewToggle mode={viewMode} onChange={setViewMode} />
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="mr-2 h-4 w-4" />
              {t('common:button.refresh')}
            </Button>
            {canWrite && (
              <Button size="sm" onClick={handleCreateClick}>
                <Plus className="mr-2 h-4 w-4" />
                {t('list.createNew')}
              </Button>
            )}
          </div>
        }
      />

      {/* Loading state */}
      {isLoading && (
        <Card>
          <CardContent className="py-6">
            <TableSkeleton rows={3} cols={4} />
          </CardContent>
        </Card>
      )}

      {/* Error state */}
      {error && <ApiErrorDisplay error={error} onRetry={() => refetch()} />}

      {/* Spec 352 §3: Filterleiste — ueber Empty-State/Grid, damit sie auch im
          gefilterten Leerzustand sichtbar bleibt (Recovery-Pfad). */}
      {!isLoading && !error && (services.length > 0 || hasActiveFilters) && (
        <ServiceFilterBar
          filters={filters}
          hasActiveFilters={hasActiveFilters}
          onSetMerchant={setMerchant}
          onSetGw={setGw}
          onSetFlow={setFlow}
          onSetIqServiceId={setIqServiceId}
          onReset={reset}
          services={services}
          truncated={data?.truncated}
          unavailable={data?.unavailable}
        />
      )}

      {/* Spec 352 §6: Kontext-Banner aus appliedFilters.gwContext (BFF-seitig ueber
          die volle Treffermenge gerechnet; das FE rendert nur). */}
      {!isLoading && !error && showGwBanner && gwContext && (
        <div
          role="status"
          className="mb-4 flex items-start justify-between gap-2 rounded-md border border-sky-200 bg-sky-50 p-3 text-sm text-sky-900"
        >
          <div className="flex items-start gap-2">
            <Link2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>
              {t('banner.gwServices', { count: gwContext.matchedServices })}
              {' · '}
              {t('banner.gwEntries', { count: gwContext.matchedEntries })}
              {' · '}
              {t('banner.gwPositions', {
                primary: gwContext.positions.primary,
                fallback: gwContext.positions.fallback,
              })}
              {data?.truncated && (
                <span className="ml-1 text-sky-700">({t('banner.gwPartial')})</span>
              )}
            </span>
          </div>
          <button
            type="button"
            onClick={() => setDismissedGwBanner(filters.gatewayServiceId ?? null)}
            aria-label={t('banner.dismiss')}
            className="shrink-0 rounded-sm hover:bg-sky-100"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Leerzustand — echte-Null (keine Filter): Create-CTA wie bisher. */}
      {!isLoading && !error && services.length === 0 && !hasActiveFilters && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">{t('list.empty')}</p>
            <Button className="mt-4" onClick={handleCreateClick}>
              <Plus className="mr-2 h-4 w-4" />
              {t('list.createFirst')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Leerzustand — gefilterte-Null: keine Create-CTA (es gibt Services, sie
          matchen nur nicht); Filterleiste + Reset bleiben oben sichtbar. */}
      {!isLoading && !error && services.length === 0 && hasActiveFilters && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">{t('list.emptyFiltered')}</p>
          </CardContent>
        </Card>
      )}

      {/* Spec 352 §4: Kachel↔Tabelle — beide Sichten ueber DIESELBE gefilterte +
          paginierte Liste; der Toggle tauscht nur den Renderer (Paritaet). */}
      {!isLoading && !error && services.length > 0 && viewMode === 'card' && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {services.map((service) => (
            <ServiceCard
              key={service.serviceId}
              service={service}
              onEdit={canWrite ? handleEditClick : undefined}
              onDelete={canWrite ? handleDeleteClick : undefined}
              showMerchant={showMerchant}
            />
          ))}
        </div>
      )}

      {!isLoading && !error && services.length > 0 && viewMode === 'table' && (
        <Card>
          <ServiceTable
            services={services}
            onEdit={canWrite ? handleEditClick : undefined}
            onDelete={canWrite ? handleDeleteClick : undefined}
            showMerchant={showMerchant}
          />
        </Card>
      )}

      {/* Spec 352 §0: echter Cursor-Stack-Pager statt passivem Hinweis */}
      {!isLoading && !error && data && (
        <CursorPagination
          hasMore={data.pagination.hasMore}
          nextCursor={data.pagination.nextCursor ?? null}
          onNext={handleNextPage}
          onPrevious={handlePreviousPage}
          hasPrevious={hasPreviousPage}
          isLoading={isLoading}
        />
      )}

      {/* Create/Edit Form Dialog */}
      <ServiceForm
        service={editingService}
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open);
          if (!open) setEditingService(null);
        }}
        onSubmitCreate={handleCreate}
        onSubmitUpdate={handleUpdate}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmation
        service={deletingService}
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          setDeleteDialogOpen(open);
          if (!open) setDeletingService(null);
        }}
        onConfirm={handleDeleteConfirm}
        isDeleting={deleteMutation.isPending}
      />
    </div>
  );
}
